// CallSummaryCompose.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ConsoleHeader, {
  ConsoleHeaderMeta,
  LedgerFrame,
} from "../../components/common/ConsoleHeader.tsx";
import { designTokens } from "../../theme.ts";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useCallSummaryApi } from "../../hooks/useCallSummaryApi.ts";
import { CallSummary } from "./types.ts";
import dayjs from "dayjs";

function formatDate(iso: string): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(
    2,
    "0"
  )}`;
}

function clampText(s: string, n: number) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

export default function CallSummaryCompose() {
  const { updateCallSummary, getAllCallSummary, deleteCallSummary } = useCallSummaryApi();

  const [rows, setRows] = useState<CallSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<CallSummary | null>(null);

  // memo edit
  const [memoDraft, setMemoDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<string | null>(null);

  // delete confirm
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CallSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const memoChanged = useMemo(() => {
    if (!selected) return false;
    return (selected.usermemo ?? "") !== memoDraft;
  }, [selected, memoDraft]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "calldatetimestamp",
        headerName: "전화 일자",
        flex: 1,
        // valueFormatter: (v) => formatDate(String(v.value ?? "")),
        renderCell: (params) => formatDate(String(params.value ?? "")),
      },
      { field: "personincharge", headerName: "담당자", flex: 1 },
      { field: "customer", headerName: "고객", flex: 1 },
      {
        field: "summary",
        headerName: "AI 요약",
        flex: 2,
        sortable: false,
        renderCell: (params) => clampText(String(params.value ?? ""), 120),
      },
      {
        field: "modifiedtimestamp",
        headerName: "수정 일자",
        flex: 1,
        // valueFormatter: (v) => formatDate(String(v.value ?? "")),
        renderCell: (params) => formatDate(String(params.value ?? "")),

      },
      {
        field: "__actions",
        headerName: "",
        sortable: false,
        filterable: false,
        width: 220,
        renderCell: (params) => {
          const row = params.row as CallSummary;
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant="outlined" onClick={() => openDetail(row)}>
                Detail
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => openDeleteConfirm(row)}
              >
                Delete
              </Button>
            </Stack>
          );
        },
      },
    ],
    []
  );

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    async function refresh() {
        setLoading(true);
        setError(null);
        try {
            const list = await getAllCallSummary();

            const cleaned = (list ?? []).filter((r): r is CallSummary => !!r && typeof r === "object");

            // calldatetimestamp 내림차순 정렬
            const sorted = [...cleaned].sort((a, b) => {
            const ta = new Date(a.calldatetimestamp).getTime();
            const tb = new Date(b.calldatetimestamp).getTime();

            // 파싱 실패(NaN) 대비: 문자열 비교로 fallback (ISO면 잘 동작)
            if (!Number.isFinite(ta) || !Number.isFinite(tb)) {
                return (b.calldatetimestamp || "").localeCompare(a.calldatetimestamp || "");
            }
            return tb - ta;
            });

            setRows(sorted);
        } catch (e: any) {
            setError(e?.message ?? "Failed to load call summaries.");
        } finally {
            setLoading(false);
        }
    }


  function openDetail(row: CallSummary) {
    setSelected(row);
    setMemoDraft(row.usermemo ?? "");
    setSaveError(null);
    setSaveOk(null);
    setDetailOpen(true);
  }

  function closeDetail() {
    setDetailOpen(false);
    setSelected(null);
    setMemoDraft("");
    setSaving(false);
    setSaveError(null);
    setSaveOk(null);
  }

  async function onUpdateMemo() {
    if (!selected) return;
    setSaving(true);
    setSaveError(null);
    setSaveOk(null);

    try {
      // 여기만 수정 필요할 수 있음:
      // updateCallSummary가 (id, patch) 형태가 아니면 시그니처에 맞춰 변경
      const updated: CallSummary = {
        ...selected,
        usermemo: memoDraft,
        modifiedtimestamp: dayjs().tz('Asia/Seoul')
                                    .format('YYYY-MM-DDTHH:mm:ss')
      }
      await updateCallSummary(updated);
      setSelected(updated)

      setSaveOk("Saved.");
      await refresh();
    } catch (e: any) {
      setSaveError(e?.message ?? "Failed to update memo.");
    } finally {
      setSaving(false);
    }
  }

  function openDeleteConfirm(row: CallSummary) {
    setDeleteTarget(row);
    setDeleteError(null);
    setDeleteOpen(true);
  }

  function closeDeleteConfirm() {
    setDeleteOpen(false);
    setDeleteTarget(null);
    setDeleting(false);
    setDeleteError(null);
  }

  async function onDeleteConfirmed() {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);

    try {
      // 여기만 수정 필요할 수 있음:
      // deleteCallSummary가 (id) 형태가 아니면 시그니처에 맞춰 변경
      await deleteCallSummary(deleteTarget.id);

      // 목록에서 제거
      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));

      // detail 열려있고 같은 id면 닫기
      if (selected?.id === deleteTarget.id) {
        closeDetail();
      }

      closeDeleteConfirm();
    } catch (e: any) {
      setDeleteError(e?.message ?? "Failed to delete.");
      setDeleting(false);
    }
  }

  return (
    <Box sx={{ fontFamily: designTokens.fonts.sans }}>
      <ConsoleHeader
        code="MARS · CS · CALL-LOG"
        title="상담 기록"
        meta={<ConsoleHeaderMeta recordCountLabel="상담 기록" count={rows.length} />}
        actions={
          <Button
            onClick={refresh}
            disabled={loading}
            variant="outlined"
            startIcon={<RefreshIcon />}
          >
            새로고침
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <LedgerFrame label="상담 기록 목록" rows={rows.length}>
        <Box sx={{ height: 620, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            getRowId={(r) => r.id}
          />
        </Box>
      </LedgerFrame>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={closeDetail} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pr: 6 }}>
          Call Detail
          <IconButton
            aria-label="close"
            onClick={closeDetail}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {!selected ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Call Date: {formatDate(selected.calldatetimestamp)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  In Charge: {selected.personincharge}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customer: {selected.customer}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Modified: {formatDate(selected.modifiedtimestamp)}
                </Typography>
              </Stack>

              <Divider />

              {/* 좌/우 50:50 (모바일은 세로 스택) */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                {/* Left: summary (read-only) */}
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "12px", p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    AI 요약
                  </Typography>
                  <Box
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      typography: "body2",
                      minHeight: 360,
                    }}
                  >
                    {selected.summary || "-"}
                  </Box>
                </Box>

                {/* Right: usermemo (editable) */}
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "12px", p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    메모 (수정 가능)
                  </Typography>

                  <TextField
                    value={memoDraft}
                    onChange={(e) => setMemoDraft(e.target.value)}
                    placeholder="Write your memo here..."
                    multiline
                    minRows={14}
                    fullWidth
                  />

                  <Stack sx={{ mt: 2 }} spacing={1}>
                    {saveError && <Alert severity="error">{saveError}</Alert>}
                    {saveOk && <Alert severity="success">{saveOk}</Alert>}
                  </Stack>
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineIcon />}
            disabled={!selected || saving}
            onClick={() => selected && openDeleteConfirm(selected)}
          >
            Delete
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button onClick={closeDetail} disabled={saving}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={onUpdateMemo}
            disabled={!selected || saving || !memoChanged}
          >
            {saving ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteOpen} onClose={closeDeleteConfirm} fullWidth maxWidth="sm">
        <DialogTitle>Delete Call Summary</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            아래 항목을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </Typography>

          <Box sx={{ mt: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: "12px" }}>
            <Typography variant="body2" color="text.secondary">
              ID: {deleteTarget?.id ?? "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Call Date: {deleteTarget ? formatDate(deleteTarget.calldatetimestamp) : "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customer: {deleteTarget?.customer ?? "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              In Charge: {deleteTarget?.personincharge ?? "-"}
            </Typography>
          </Box>

          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDeleteConfirm} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={onDeleteConfirmed}
            disabled={!deleteTarget || deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
